import { useEffect, useRef, type ReactNode } from 'react';
import { focusRouteStart } from './focusRouteStart';

/**
 * Route title + focus management (blueprint §5 gate —
 * docs/redesign/DESIGN_BLUEPRINT.md): every redesign route sets
 * `document.title` and moves focus to its unique `<h1 tabindex="-1">` on
 * mount, so SPA navigation is announced like a page load. The h1 is not
 * in the tab order (tabIndex -1 is focus-by-script only).
 *
 * Visible rings stay on `:focus-visible` for real controls
 * (`primitives.css`). This scripted focus must not paint one — see
 * `focusRouteStart`.
 *
 * Render exactly once per route, inside the route's single <main>.
 */
export function RouteHead({ title, children }: { title: string; children: ReactNode }) {
  const headingRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    document.title = `${title} · Vidi`;
    focusRouteStart(headingRef.current);
  }, [title]);

  return (
    <h1 ref={headingRef} tabIndex={-1}>
      {children}
    </h1>
  );
}
